'use client';

import {
	Flex,
	MantineTheme,
	MantineThemeOverride,
	createTheme,
	rem,
	Text,
	Divider,
	Anchor,
	Popover,
	Accordion,
	Input,
	TextInput,
	Card,
	Select,
	Textarea,
	Checkbox,
	NumberInput,
	FileInput,
	Button,
	Breadcrumbs,
	ThemeIcon,
	List,
	Modal,
	Tooltip,
	PasswordInput,
	NavLink,
	Menu,
	Table,
	Alert,
	Badge,
} from '@mantine/core';
import { yantramanav } from './assets/fonts/fonts';
import { SlArrowDown } from 'react-icons/sl';
import { DateInput } from '@mantine/dates';
import { IoCheckmarkSharp } from 'react-icons/io5';
import { Dropzone } from '@mantine/dropzone';
import styles from './assets/styles/mantine.module.scss';
import 'dayjs/locale/pl';

const components: MantineThemeOverride['components'] = {
	Flex: Flex.extend({
		defaultProps: {
			align: 'center',
		},
	}),
	Text: Text.extend({
		defaultProps: {
			size: 'lg',
		},
	}),
	Divider: Divider.extend({
		defaultProps: {
			color: 'gray.2',
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {
			c: 'gray.5',
		},
	}),
	Popover: Popover.extend({
		defaultProps: {
			radius: 0,
			withArrow: false,
		},
	}),
	Accordion: Accordion.extend({
		classNames: {
			root: styles.accordionRoot,
			item: styles.accordionItem,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
		},
		defaultProps: {
			chevronSize: 24,
		},
	}),
	Input: Input.extend({
		defaultProps: {
			radius: 0,
		},
		classNames: {
			input: styles.inputInput,
		},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
			withAsterisk: false,
		},
		classNames: {
			label: styles.inputLabel,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
			withAsterisk: false,
		},
		classNames: {
			label: styles.inputLabel,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
		},
		classNames: {
			input: styles.inputInput,
			label: styles.inputLabel,
		},
	}),
	Card: Card.extend({
		defaultProps: {
			bg: 'dark.0',
			radius: 0,
			withBorder: true,
			p: 'md',
		},
	}),
	Select: Select.extend({
		defaultProps: {
			rightSection: <SlArrowDown />,
			radius: 0,
			size: 'lg',
			withCheckIcon: false,
			allowDeselect: false,
		},
		classNames: {
			label: styles.inputLabel,
			input: styles.inputInput,
			option: styles.selectOption,
		},
	}),
	Checkbox: Checkbox.extend({
		defaultProps: {
			size: 'md',
		},
		classNames: {
			label: styles.checkboxLabel,
		},
	}),
	CheckboxGroup: Checkbox.Group.extend({
		defaultProps: {},
		classNames: {
			label: styles.checkboxLabel,
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
			hideControls: true,
			clampBehavior: 'blur',
		},
		classNames: {
			label: styles.inputLabel,
			input: styles.inputInput,
		},
	}),
	FileInput: FileInput.extend({
		defaultProps: {
			radius: 0,
			size: 'lg',
		},
		classNames: {
			label: styles.inputLabel,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			size: 'lg',
		},
		classNames: {
			label: styles.buttonLabel,
		},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {
			radius: 0,
		},
	}),
	DateInput: DateInput.extend({
		defaultProps: {
			size: 'lg',
			locale: 'pl',
		},
		classNames: {
			label: styles.inputLabel,
		},
	}),
	Breadcrumbs: Breadcrumbs.extend({
		defaultProps: {
			separator: '-',
		},
	}),
	ThemeIcon: ThemeIcon.extend({
		defaultProps: {
			color: 'blue.8',
			radius: 'xl',
			size: 'lg',
		},
	}),
	List: List.extend({
		defaultProps: {
			spacing: 'sm',
			icon: (
				<ThemeIcon size={20} radius="xl" color="red.6">
					<IoCheckmarkSharp
						style={{
							width: 12,
							height: 12,
						}}
					/>
				</ThemeIcon>
			),
		},
	}),
	Modal: Modal.extend({
		defaultProps: {
			centered: true,
			size: 'xl',
		},
		classNames: {
			title: styles.modalTitle,
		},
	}),
	Dropzone: Dropzone.extend({
		classNames: {
			root: styles.dropzoneRoot,
		},
	}),
	NavLink: NavLink.extend({
		classNames: {
			root: styles.navLinkRoot,
			label: styles.navLinkLabel,
		},
	}),
	Menu: Menu.extend({
		classNames: {
			item: styles.menuItem,
			dropdown: styles.menuDropdown,
		},
	}),
	Table: Table.extend({
		defaultProps: {
			stripedColor: 'gray.1',
			highlightOnHoverColor: 'dark.2',
		},
		classNames: {
			table: styles.tableTable,
			thead: styles.tableThead,
			th: styles.tableTh,
			td: styles.tableTd,
		},
	}),
	Alert: Alert.extend({
		classNames: {
			title: styles.alertTitle,
			label: styles.alertLabel,
			body: styles.alertBody,
			message: styles.alertMessage,
		},
	}),
	Badge: Badge.extend({
		classNames: {
			root: styles.badgeRoot,
			label: styles.badgeLabel,
		},
	}),
};

const colors: MantineThemeOverride['colors'] = {
	gray: [
		'#fefdfd',
		'#eaeef0',
		'#eaecf0',
		'#f3f3f3',
		'#98a2b3',
		'#6a6a6a',
		'#667085',
		'#344054',
		'#1d2939',
		'#545453',
	],
	dark: [
		'#f8fafc',
		'#eef2f6',
		'#E3E8EF',
		'#cdd5df',
		'#9aa4b2',
		'#697586',
		'#697586',
		'#364152',
		'#202939',
		'#202939',
	],
	green: [
		'#e6f6f4',
		'#e3fbcc',
		'#b0e4dd',
		'#00a991',
		'#009883',
		'#16b364',
		'#51a018',
		'#006557',
		'#004c41',
		'#07353f',
	],
	blue: [
		'#F0F9FF',
		'#e0f2fe',
		'#026AA2',
		'#026AA2',
		'#026AA2',
		'#026AA2',
		'#0086C9',
		'#026AA2',
		'#233a65',
		'#026AA2',
	],
	red: [
		'#E30613',
		'#FEE4E2',
		'#E30613',
		'#E30613',
		'#E30613',
		'#E30613',
		'#ab2926',
		'#E30613',
		'#E30613',
		'#E30613',
	],
};

const fontSizes: MantineTheme['fontSizes'] = {
	xs: rem(11),
	sm: rem(12),
	md: rem(14),
	lg: rem(16),
	xl: rem(18),
};

const lineHeights: MantineTheme['lineHeights'] = {
	xs: rem(18),
	sm: rem(18),
	md: rem(20),
	lg: rem(24),
	xl: rem(30),
};

const headings: MantineThemeOverride['headings'] = {
	fontFamily: yantramanav.style.fontFamily,
	fontWeight: '700',
	sizes: {
		h1: {
			fontSize: rem(30),
			lineHeight: rem(44),
		},
		h2: {
			fontSize: rem(24),
			lineHeight: rem(32),
		},
		h3: {
			fontSize: rem(18),
			lineHeight: rem(28),
		},
		h4: {
			fontSize: rem(16),
			lineHeight: rem(20),
		},
	},
};

const spacing: MantineThemeOverride['spacing'] = {
	xs: rem(4),
	sm: rem(8),
	md: rem(16),
	lg: rem(24),
	xl: rem(32),
	xxl: rem(50),
};

export const theme = createTheme({
	fontFamily: yantramanav.style.fontFamily,
	components,
	colors,
	fontSizes,
	lineHeights,
	headings,
	spacing,
	white: '#ffffff',
	black: '#112126',
	primaryColor: 'red',
	cursorType: 'pointer',
	primaryShade: 6,
});
